import { createAppSlice } from "../../app/createAppSlice"
import { createEntityAdapter } from '@reduxjs/toolkit'
import { startAppListening } from "../../app/listenerMiddleware"
import { selectCurrentUserApiToken } from "../currentUser/currentUserSlice";
import { loadSavedSearches, updateSearch } from "./savedSearchAPI"

const fetchSavedSearchesAPI = async (apiToken) => {
    let response = await loadSavedSearches(apiToken);
    if (response?.success && response.userSearches) {
        let savedSearchesCollection = response.userSearches;
        return savedSearchesCollection;
    }
    return [];
}

//entity: {
//      'id': integer
//      'userid': integer
//      'foi': integer
//      'label': string
//      'source': string
//      'searchstring': string
//      'searchparameters': object
//      'autocheck': boolean
//      'removed': boolean (false)
//      'lastcheckeddate': dateTime
//      'datecreated': dateTime
//    }

//key uses: to provide savedSearches regarding updates to Resources
const savedSearchesAdapter = createEntityAdapter();

const initialState = savedSearchesAdapter.getInitialState({
    status: 'idle',
    error: null
});

const savedSearchesSlice = createAppSlice({
    name: "savedSearches",
    initialState,
    reducers: create => ({
        savedSearchAdded: create.preparedReducer(
            (savedSearchBody) => {
                return {
                    payload: {
                        'id': savedSearchBody.id,
                        'userid': savedSearchBody.userid,
                        'foi': savedSearchBody.foi,
                        'label': savedSearchBody.label,
                        'source': savedSearchBody.source,
                        'searchstring': savedSearchBody.searchstring,
                        'searchparameters': savedSearchBody.searchparameters,
                        'autocheck': savedSearchBody.autocheck ?? true,
                        'removed': savedSearchBody.removed || false,
                        'lastcheckeddate': savedSearchBody.lastcheckeddate,
                        'datecreated': savedSearchBody.datecreated
                    }
                }
            },
            savedSearchesAdapter.addOne
        ),
        savedSearchUpdated: create.reducer((state, action) => {
            const { id, userid, foi, label, source, searchstring, searchparameters, autocheck, removed, lastcheckeddate, datecreated } = action.payload;
            savedSearchesAdapter.updateOne(state,
                { id, changes: { userid, foi, label, source, searchstring, searchparameters, autocheck, removed, lastcheckeddate, datecreated } })
        }),
        fetchSavedSearches: create.asyncThunk(
            async (_, { getState }) => {
                const state = getState();
                const apiToken = selectCurrentUserApiToken(state);
                const savedSearchesCollection = await fetchSavedSearchesAPI(apiToken);
                return savedSearchesCollection;
            },
            {
                /*
                options: {
                    condition(arg, thunkApi) {
                        const { savedSearches } = thunkApi.getState()
                        if (savedSearches.status !== 'idle') {
                            return false
                        }
                    }
                },
                */
                pending: state => {
                    state.status = "loading"
                },
                fulfilled: (state, action) => {
                    state.status = "succeeded"
                    savedSearchesAdapter.setAll(state, action.payload)
                },
                rejected: (state, action) => {
                    state.status = "failed"
                    state.error = action.error.message ?? "Unknown Error"
                },
            },
        ),
    }),
    selectors: {
        selectSavedSearchesStatus: savedSearches => savedSearches.status,
        selectSavedSearchesError: savedSearches => savedSearches.error
    },
})

export const { savedSearchAdded, savedSearchUpdated, fetchSavedSearches } = savedSearchesSlice.actions

export const { selectSavedSearchesStatus, selectSavedSearchesError } = savedSearchesSlice.selectors

export const {
    selectAll: selectAllSavedSearches,
    selectById: selectSavedSearchById
} = savedSearchesAdapter.getSelectors(state => state.savedSearches);

export const addSavedSearchesListeners = () => {
    startAppListening({
        actionCreator: savedSearchUpdated,
        effect: async (action, listenerApi) => {
            const state = listenerApi.getState();
            const apiToken = selectCurrentUserApiToken(state);
            console.log("savedSearches listener was triggered")
            try {
                const updatedSavedSearch = action.payload;
                await updateSearch(apiToken,
                    updatedSavedSearch.id, updatedSavedSearch);
                listenerApi.dispatch(fetchSavedSearches());
            } catch (err) {
                console.log("FAILURE of updateSearch: ", err)
            }
        }
    })
}

export default savedSearchesSlice
